import './PrefAndConsents.scss';

import { marked } from 'marked';
import { useContext, useEffect, useState } from 'react';
import { Button, Form, Spinner } from 'react-bootstrap';
import { faCircleInfo } from '@fortawesome/pro-regular-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';

import { BRIDGE_BIO_CAMPAIGN_CODE } from '@constants/bridgeBio';
import { CONSENT_URI, MYOME_SUPPORT_EMAIL, PRODUCT_NOTIFICATIONS_CONSENT } from '@constants/constants';
import { IUserConsent } from '@customTypes/user';
import useApp from '@hooks/useApp';
import { fetchAllConsents, fetchUsersConsents, updateUserConsent } from '@store/actions';
import { Context } from '@store/store';

import ViewConsentModal from './ViewConsentModal';

export const PrefAndConsents = () => {
  const [state] = useContext(Context);
  const { campaignCode, isLoadingUserInfo } = useApp();
  const showResearchConsent = campaignCode !== BRIDGE_BIO_CAMPAIGN_CODE && !isLoadingUserInfo;
  const [consents, setConsents] = useState<IUserConsent[]>([]);
  const [viewContent, setViewContent] = useState<string | null>(null);
  const [loadingConsentUri, setLoadingConsentUri] = useState<string | null>(null);
  const [failedConsentUri, setFailedConsentUri] = useState<string | null>(null);
  const [consentTextByUri, setConsentTextByUri] = useState<Record<string, string>>({});
  const [productNotificationConsentStatus, setProductNotificationConsentStatus] = useState(false);
  const [researchConsentStatus, setResearchConsentStatus] = useState(false);
  const [researchConsentUri, setResearchConsentUri] = useState<string | null>(null);
  const [transactionalSmsConsentStatus, setTransactionalSmsConsentStatus] = useState(false);
  const [transactionalSmsConsentUri, setTransactionalSmsConsentUri] = useState<string | null>(null);
  const [marketingConsentStatus, setMarketingConsentStatus] = useState(false);
  const [marketingConsentUri, setMarketingConsentUri] = useState<string | null>(null);

  marked.use({
    mangle: false,
    headerIds: false,
  });

  useEffect(() => {
    if (state.userId) {
      fetchUsersConsents(state.userId).then(res => {
        if (res.consents) {
          // HIE is collected at order time; hide the broken read-only entry on My Account.
          const consentsToDisplay = res.consents.filter(
            consent => !consent.consent_uri.startsWith(CONSENT_URI.HIE_ACCESS_TREATMENT),
          );

          const productConsentIndex = consentsToDisplay.findIndex(i => i.consent_uri === PRODUCT_NOTIFICATIONS_CONSENT);
          if (productConsentIndex >= 0) {
            const productConsent = consentsToDisplay.splice(productConsentIndex, 1)[0];
            setProductNotificationConsentStatus(productConsent?.is_active || false);
          }

          const researchConsentIndex = consentsToDisplay.findIndex(i =>
            i.consent_uri.startsWith(CONSENT_URI.CONTACT_RESEARCH_OPPORTUNITIES),
          );
          if (researchConsentIndex >= 0) {
            const researchConsent = consentsToDisplay.splice(researchConsentIndex, 1)[0];
            setResearchConsentStatus(researchConsent?.is_active || false);
            setResearchConsentUri(researchConsent?.consent_uri || null);
          }

          const transactionalSmsConsentIndex = consentsToDisplay.findIndex(i =>
            i.consent_uri.startsWith(CONSENT_URI.CONTACT_TRANSACTIONAL_SMS),
          );
          if (transactionalSmsConsentIndex >= 0) {
            const transactionalSmsConsent = consentsToDisplay.splice(transactionalSmsConsentIndex, 1)[0];
            setTransactionalSmsConsentStatus(transactionalSmsConsent?.is_active || false);
            setTransactionalSmsConsentUri(transactionalSmsConsent?.consent_uri || null);
          }

          const marketingConsentIndex = consentsToDisplay.findIndex(i =>
            i.consent_uri.startsWith(CONSENT_URI.CONTACT_MARKETING),
          );
          if (marketingConsentIndex >= 0) {
            const marketingConsent = consentsToDisplay.splice(marketingConsentIndex, 1)[0];
            setMarketingConsentStatus(marketingConsent?.is_active || false);
            setMarketingConsentUri(marketingConsent?.consent_uri || null);
          }

          setConsents(consentsToDisplay);
        }
      });
    }
  }, [state.userId]);

  useEffect(() => {
    fetchAllConsents(['CONTACT__RESEARCH_OPPORTUNITIES', 'CONTACT__TRANSACTIONAL_SMS', 'CONTACT__MARKETING']).then(
      response => {
        const researchConsent = response.find((i: IUserConsent) =>
          i.consent_uri.startsWith(CONSENT_URI.CONTACT_RESEARCH_OPPORTUNITIES),
        );
        const transactionalSmsConsent = response.find((i: IUserConsent) =>
          i.consent_uri.startsWith(CONSENT_URI.CONTACT_TRANSACTIONAL_SMS),
        );
        const marketingConsent = response.find((i: IUserConsent) =>
          i.consent_uri.startsWith(CONSENT_URI.CONTACT_MARKETING),
        );

        setResearchConsentUri(prev => prev || researchConsent?.consent_uri || null);
        setTransactionalSmsConsentUri(prev => prev || transactionalSmsConsent?.consent_uri || null);
        setMarketingConsentUri(prev => prev || marketingConsent?.consent_uri || null);
      },
    );
  }, []);

  const onSpecificConsentToggle = (consentUri: string, currentStatus: boolean, setStatus: (val: boolean) => void) => {
    if (!state.userId) return;

    const payloadConsents: { consents_granted: string[]; consents_revoked: string[] } = {
      consents_granted: consents.map(m => m.consent_uri),
      consents_revoked: [],
    };

    // Keep other consents' current state when one toggle changes.
    const preserveConsent = (uri: string | null, isActive: boolean) => {
      if (!uri || uri === consentUri) return;
      if (isActive) payloadConsents.consents_granted.push(uri);
      else payloadConsents.consents_revoked.push(uri);
    };

    preserveConsent(PRODUCT_NOTIFICATIONS_CONSENT, productNotificationConsentStatus);
    if (showResearchConsent) {
      preserveConsent(researchConsentUri, researchConsentStatus);
    }
    preserveConsent(transactionalSmsConsentUri, transactionalSmsConsentStatus);
    preserveConsent(marketingConsentUri, marketingConsentStatus);

    if (currentStatus) {
      payloadConsents.consents_revoked.push(consentUri);
    } else {
      payloadConsents.consents_granted.push(consentUri);
    }

    updateUserConsent(state.userId, payloadConsents).then(() => {
      setStatus(!currentStatus);
    });
  };

  const onConsentToggle = () => {
    onSpecificConsentToggle(
      PRODUCT_NOTIFICATIONS_CONSENT,
      productNotificationConsentStatus,
      setProductNotificationConsentStatus,
    );
  };

  const onResearchConsentToggle = () => {
    if (!researchConsentUri) return;
    onSpecificConsentToggle(researchConsentUri, researchConsentStatus, setResearchConsentStatus);
  };

  const onTransactionalSmsConsentToggle = () => {
    if (!transactionalSmsConsentUri) return;
    onSpecificConsentToggle(
      transactionalSmsConsentUri,
      transactionalSmsConsentStatus,
      setTransactionalSmsConsentStatus,
    );
  };

  const onMarketingConsentToggle = () => {
    if (!marketingConsentUri) return;
    onSpecificConsentToggle(marketingConsentUri, marketingConsentStatus, setMarketingConsentStatus);
  };

  const renderConsentText = (consent: IUserConsent) => {
    setFailedConsentUri(null);

    const cachedText = consent.consent_text || consentTextByUri[consent.consent_uri];
    if (cachedText) {
      setViewContent(marked.parse(cachedText) as string);
      return;
    }

    // Derive the consent type code from the URI (e.g. myome:consent:GENERAL-1.0.0 → GENERAL)
    // instead of a hardcoded allowlist, so View works for any remaining consent row.
    const consentType = consent.consent_uri.replace(/^myome:consent:/, '').replace(/-[0-9.]+$/, '');
    if (!consentType) {
      setFailedConsentUri(consent.consent_uri);
      return;
    }

    // The patient consent list carries no consent_text, so every first view waits on this fetch.
    setLoadingConsentUri(consent.consent_uri);
    // Errors surface inline on the row below, so skip the global toast.
    fetchAllConsents([consentType], { suppressErrorToast: true })
      .then(response => {
        const match =
          response?.find((c: IUserConsent) => c.consent_uri === consent.consent_uri) ||
          response?.find((c: IUserConsent) => c.name === consent.name) ||
          response?.[0];

        if (!match?.consent_text) {
          setFailedConsentUri(consent.consent_uri);
          return;
        }

        setConsentTextByUri(prev => ({ ...prev, [consent.consent_uri]: match.consent_text as string }));
        setViewContent(marked.parse(match.consent_text) as string);
      })
      .catch(() => setFailedConsentUri(consent.consent_uri))
      .finally(() => setLoadingConsentUri(null));
  };

  return (
    <>
      <div className='py-3 px-3 px-md-4 bg-white rounded-4 d-flex flex-column pref-and-consents'>
        <div className='d-flex py-4 px-3 border-bottom'>
          <Form.Check
            type='switch'
            id='custom-switch'
            checked={productNotificationConsentStatus}
            onChange={onConsentToggle}
            aria-describedby='consentToggleDesc'
          />
          <div className='d-flex flex-column align-items-start'>
            <Form.Label htmlFor='custom-switch' className='fs-6 fw-bold mb-0'>
              Contact me with information about MyOme
            </Form.Label>
            <Form.Text id='consentToggleDesc' className='fs-xs mt-0'>
              I’d like to opt in to receive marketing and other promotional information from MyOme
            </Form.Text>
          </div>
        </div>
        {showResearchConsent && (
          <div className='d-flex py-4 px-3 border-bottom'>
            <Form.Check
              type='switch'
              id='research-switch'
              checked={researchConsentStatus}
              onChange={onResearchConsentToggle}
              aria-describedby='researchToggleDesc'
            />
            <div className='d-flex flex-column align-items-start'>
              <Form.Label htmlFor='research-switch' className='fs-6 fw-bold mb-0'>
                Contact me about research studies or clinical trial opportunities
              </Form.Label>
              <Form.Text id='researchToggleDesc' className='fs-xs mt-0'>
                By opting-in you are agreeing to being contacted by MyOme about relevant research opportunities or
                clinical trials. Participation is voluntary and would require additional consent before any information
                is shared with researchers.
              </Form.Text>
            </div>
          </div>
        )}
        <div className='d-flex py-4 px-3 border-bottom'>
          <Form.Check
            type='switch'
            id='transactional-sms-switch'
            checked={transactionalSmsConsentStatus}
            onChange={onTransactionalSmsConsentToggle}
          />
          <div className='d-flex flex-column align-items-start'>
            <Form.Label htmlFor='transactional-sms-switch' className='fs-6 fw-bold mb-0'>
              MyOme may contact me via phone call or text message regarding updates related to this order and testing
              services.
            </Form.Label>
          </div>
        </div>
        <div className='d-flex py-4 px-3 border-bottom'>
          <Form.Check
            type='switch'
            id='marketing-switch'
            checked={marketingConsentStatus}
            onChange={onMarketingConsentToggle}
          />
          <div className='d-flex flex-column align-items-start'>
            <Form.Label htmlFor='marketing-switch' className='fs-6 fw-bold mb-0'>
              I agree to receive marketing or promotional communications from MyOme via email, phone call, or text
              message.
            </Form.Label>
          </div>
        </div>
        <ul className='mb-0 ps-2 list-style-none pt-4 pb-3'>
          {consents.map((c, i) => {
            const isLoading = loadingConsentUri === c.consent_uri;
            return (
              <li className='d-flex flex-column align-items-start gap-1 mb-2' key={'consent_' + i}>
                <p className='fs-sm fw-bold'>{c.name}</p>
                <p className='fs-xs'>
                  I agree to the terms and conditions provided in the <strong>{c.name}</strong>.
                </p>
                <Button variant='ghost-primary' size='sm' disabled={isLoading} onClick={() => renderConsentText(c)}>
                  {isLoading ? (
                    <Spinner as='span' animation='border' size='sm' role='status' aria-hidden='true' className='me-2' />
                  ) : (
                    <FontAwesomeIcon icon={faCircleInfo} className='me-2' />
                  )}
                  View
                </Button>
                {failedConsentUri === c.consent_uri && (
                  <p className='fs-xs text-danger mb-0'>
                    We could not load this consent. Please try again or contact us at{' '}
                    <a href={`mailto:${MYOME_SUPPORT_EMAIL}`}>{MYOME_SUPPORT_EMAIL}</a>.
                  </p>
                )}
              </li>
            );
          })}
        </ul>
      </div>

      {!!viewContent && <ViewConsentModal content={viewContent} onClose={() => setViewContent(null)} />}
    </>
  );
};
